import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './profile.css';
import cameraIcon from '../Images/userLogo4.png';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2'; // Add this import

function Profile({ isOpen = false, onClose = () => {} }) {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(cameraIcon);
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({ name: '', email: '', phone: '' });
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const authToken = localStorage.getItem('token');

  const handleDeleteAccount = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Deactivate Account',
      text: 'Are you sure you want to deactivate your account? This action will log you out immediately.',
      showCancelButton: true,
      confirmButtonText: 'Yes, deactivate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      width: "400px",
      customClass: {
        popup: 'ai-swal-popup',
        title: 'ai-swal-title',
        content: 'ai-swal-content',
        confirmButton: 'ai-swal-confirm-button',
        cancelButton: 'ai-swal-cancel-button'
      }
    });

    if (!result.isConfirmed) {
      return; // User cancelled
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (!authToken) {
        Swal.fire({
          icon: 'error',
          title: 'Not Logged In',
          text: 'You are not logged in. Please log in to delete your account.',
          width: "300px",
          customClass: {
            popup: 'ai-swal-popup',
            title: 'ai-swal-title',
            content: 'ai-swal-content',
            confirmButton: 'ai-swal-confirm-button'
          }
        });
        setLoading(false);
        navigate('/login');
        return;
      }

      const config = {
        // headers: {
        //   'Content-Type': 'application/json',
        //   'Authorization': `Bearer ${authToken}`,
        // },
      };

      const response = await axios.delete(`${apiBaseUrl}/api/auth/delete-account`);

      if (response.status === 200 && response.data.accountDeactivated) {
        Swal.fire({
          icon: 'success',
          title: 'Account Deactivated',
          text: response.data.message,
          timer: 2000,
          timerProgressBar: true,
          width: "300px",
          customClass: {
            popup: 'ai-swal-popup',
            title: 'ai-swal-title',
            content: 'ai-swal-content',
            confirmButton: 'ai-swal-confirm-button'
          }
        });

        // Log the user out from frontend
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Deactivation Failed',
          text: response.data.message || 'Account deactivation failed.',
          width: "300px",
          customClass: {
            popup: 'ai-swal-popup',
            title: 'ai-swal-title',
            content: 'ai-swal-content',
            confirmButton: 'ai-swal-confirm-button'
          }
        });
      }

    } catch (err) {
      console.error('Frontend Delete Account Error:', err);
      const errorMessage = err.response?.data?.message || 'An unexpected error occurred during account deactivation.';

      Swal.fire({
        icon: 'error',
        title: 'Deactivation Error',
        text: errorMessage,
        width: "300px",
        customClass: {
          popup: 'ai-swal-popup',
          title: 'ai-swal-title',
          content: 'ai-swal-content',
          confirmButton: 'ai-swal-confirm-button'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_SERVER_URL;
    setApiBaseUrl(baseUrl);
    if (authToken) {
      fetchProfile(baseUrl);
    }
  }, []);

  const fetchProfile = async (baseUrl = apiBaseUrl) => {
    try {
      const response = await axios.get(`${baseUrl}/api/auth/profile`, {
        // headers: {
        //   'Content-Type': 'application/json',
        //   Authorization: `Bearer ${authToken}`,
        // },
      });

      const userData = response.data.data;
      setUser(userData);
      setEditedUser({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
      });

      setProfileImagePreview(userData.profileImage ? `${baseUrl}${userData.profileImage}` : cameraIcon);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load profile information.',
        width: "300px",
        customClass: {
          popup: 'ai-swal-popup',
          title: 'ai-swal-title',
          content: 'ai-swal-content',
          confirmButton: 'ai-swal-confirm-button'
        }
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      Swal.fire({
        icon: 'warning',
        title: 'No Image Selected',
        text: 'Please select an image first.',
        width: "300px",
        customClass: {
          popup: 'ai-swal-popup',
          title: 'ai-swal-title',
          content: 'ai-swal-content',
          confirmButton: 'ai-swal-confirm-button'
        }
      });
      return;
    }

    if (!user._id) {
      Swal.fire({
        icon: 'warning',
        title: 'User Not Loaded',
        text: 'User not loaded yet. Please wait and try again.',
        width: "300px",
        customClass: {
          popup: 'ai-swal-popup',
          title: 'ai-swal-title',
          content: 'ai-swal-content',
          confirmButton: 'ai-swal-confirm-button'
        }
      });
      return;
    }

    const formData = new FormData();
    formData.append('profileImage', selectedFile);
    formData.append('userId', user._id);

    try {
      await axios.post(`${apiBaseUrl}/api/upload`, formData, {
        // headers: {
        //   'Content-Type': 'multipart/form-data',
        //   Authorization: `Bearer ${authToken}`,
        // },
      });

      Swal.fire({
        icon: 'success',
        title: 'Upload Successful!',
        text: 'Your profile image has been updated.',
        width: "300px",
        customClass: {
          popup: 'ai-swal-popup',
          title: 'ai-swal-title',
          content: 'ai-swal-content',
          confirmButton: 'ai-swal-confirm-button'
        }
      });

      fetchProfile();
    } catch (err) {
      console.error('Upload failed', err.response?.data || err);
      const errorMessage = err.response?.data?.message || 'Server error occurred during upload.';

      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: errorMessage,
        width: "300px",
        customClass: {
          popup: 'ai-swal-popup',
          title: 'ai-swal-title',
          content: 'ai-swal-content',
          confirmButton: 'ai-swal-confirm-button'
        }
      });
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel edit - reset to original
      setEditedUser({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isChanged =
    editedUser.name !== user.name ||
    editedUser.email !== user.email ||
    editedUser.phone !== user.phone;

  const handleSaveProfile = async () => {
    if (!editedUser.name || !editedUser.email) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Fields',
        text: 'Name and Email are required fields.',
        width: "300px",
        customClass: {
          popup: 'ai-swal-popup',
          title: 'ai-swal-title',
          content: 'ai-swal-content',
          confirmButton: 'ai-swal-confirm-button'
        }
      });
      return;
    }

    try {
      const response = await axios.put(`${apiBaseUrl}/api/auth/profile`, editedUser, {
        // headers: {
        //   'Content-Type': 'application/json',
        //   Authorization: `Bearer ${authToken}`,
        // },
      });

      setUser(response.data.data);
      setIsEditing(false);

      Swal.fire({
        icon: 'success',
        title: 'Profile Updated!',
        text: 'Your profile has been updated successfully.',
        width: "300px",
        customClass: {
          popup: 'ai-swal-popup',
          title: 'ai-swal-title',
          content: 'ai-swal-content',
          confirmButton: 'ai-swal-confirm-button'
        }
      });
    } catch (error) {
      console.error('Failed to update profile:', error);
      const errorMessage = error.response?.data?.message || 'Server error occurred during update.';

      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: errorMessage,
        width: "300px",
        customClass: {
          popup: 'ai-swal-popup',
          title: 'ai-swal-title',
          content: 'ai-swal-content',
          confirmButton: 'ai-swal-confirm-button'
        }
      });
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <>
      {isOpen && (
        <div className="profile-modal-overlay" onClick={onClose}>
          <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="profile-modal-close" onClick={onClose}>×</button>
            <div className="profile-card">
      <div className="profile-image-container" onClick={triggerFileInput}>
        <img
          src={profileImagePreview}
          alt="Profile"
          className="profile-image"
        />
        <div className="overlay">
          <img src={cameraIcon} alt="Upload" className="camera-icon" />
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          onChange={handleImageChange}
          style={{ display: 'none' }}
        />
      </div>

      <button
        onClick={handleUpload}
        className="upload-btn"
        disabled={!user._id}
      >
        Upload
      </button>

      <div className="profile-info">
        <div className="profile-info-header">
          <h2>Hello, {user?.name}</h2>
          {/* <button
            onClick={handleEditToggle}
            className={`edit-btn ${isEditing ? 'cancel-btn' : ''}`}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </button> */}
        </div>

        {!isEditing ? (
          <div className="profile-details">
            <p><strong>Email:</strong> {user?.email}</p>
            <p><strong>Phone:</strong> {user?.phone || "+XX-XXXXXXXXX"}</p>
          </div>
        ) : (
          <div className="profile-edit-form">
            <div className="input-group-profile">
              <label htmlFor="name"><strong>Name:</strong></label>
              <input
                type="text"
                id="name"
                name="name"
                value={editedUser.name}
                onChange={handleInputChange}
                className="edit-input"
              />
            </div>

            <div className="input-group-profile">
              <label htmlFor="email"><strong>Email:</strong></label>
              <input
                type="email"
                id="email"
                name="email"
                value={editedUser.email}
                onChange={handleInputChange}
                className="edit-input"
              />
            </div>

            <div className="input-group-profile">
              <label htmlFor="phone"><strong>Phone:</strong></label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={editedUser.phone}
                onChange={handleInputChange}
                placeholder="+XX-XXXXXXXXX"
                className="edit-input"
              />
            </div>

            <button
              onClick={handleSaveProfile}
              className="save-btn"
              disabled={!isChanged}
            >
              Save Changes
            </button>
          </div>
        )}
      </div>
      </div>
      </div>
      </div>
      )}
    </>
  );
}

export default Profile;